import * as React from "react";
import { IconButton, ListItemIcon, ListItemText, MenuItem, Snackbar, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SupportAgentOutlined } from "@/components/icons";
import { ChatConsentDialog } from "@/features/support/ChatConsentDialog";
import { openChat } from "@/features/support/useIntercomMessenger";

/** ``/auth/me`` ``features.addons.support_chat`` for the active org. */
export type SupportFeature = {
  available: boolean;
  org_enabled: boolean;
  consent_valid: boolean;
  member_notice_pending: boolean;
  member_notice?: { sv: string; en: string };
  org_slug?: string;
};

/** "Chat with us" in the app bar (or, as ``asMenuItem``, in the phone's
 *  overflow menu). Shown only while the active org has support chat on
 *  and the provider is offered; without a valid consent it asks first. */
export function ChatWithUsButton({
  support,
  asMenuItem = false,
  onDone,
}: {
  support: SupportFeature | undefined;
  asMenuItem?: boolean;
  /** The dialog closed or the chat opened (the overflow menu closes). */
  onDone?: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [asking, setAsking] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  if (!support?.available || !support.org_enabled) return null;
  const label = t("support.chat_with_us");
  const open = () => {
    openChat().catch(() => setFailed(true));
  };
  const start = () => {
    if (support.consent_valid) {
      open();
      onDone?.();
    } else {
      setAsking(true);
    }
  };
  const trigger = asMenuItem ? (
    <MenuItem onClick={start} aria-haspopup="dialog">
      <ListItemIcon>
        <SupportAgentOutlined fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  ) : (
    <Tooltip title={label}>
      <IconButton
        color="inherit"
        onClick={start}
        aria-label={label}
        aria-haspopup={support.consent_valid ? undefined : "dialog"}
      >
        <SupportAgentOutlined />
      </IconButton>
    </Tooltip>
  );
  return (
    <>
      {trigger}
      <ChatConsentDialog
        open={asking}
        onClose={() => {
          setAsking(false);
          onDone?.();
        }}
        onAccepted={() => {
          setAsking(false);
          open();
          onDone?.();
        }}
      />
      <Snackbar
        open={failed}
        autoHideDuration={6000}
        onClose={() => setFailed(false)}
        message={t("support.unavailable")}
      />
    </>
  );
}
